import axios from "axios";
import { Globals } from "./environtments";

// Spotify eliminó el flujo implicit grant (response_type=token responde
// 400 "response_type must be code"). Este helper implementa Authorization
// Code con PKCE, el flujo soportado para apps de navegador: no requiere
// client secret y entrega refresh_token para renovar la sesión.
const TOKEN_ENDPOINT = "https://accounts.spotify.com/api/token";

// playlist-read-* permite listar las playlists (privadas y colaborativas);
// playlist-modify-* permite agregar canciones a públicas y privadas.
const SCOPE =
  "playlist-read-private playlist-read-collaborative playlist-modify-public playlist-modify-private";

const KEYS = {
  accessToken: "access_token_spotify",
  refreshToken: "refresh_token_spotify",
  expiry: "spotify_token_expiry",
  verifier: "spotify_code_verifier",
};

function generateCodeVerifier() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const random = crypto.getRandomValues(new Uint8Array(64));
  return Array.from(random)
    .map((n) => chars[n % chars.length])
    .join("");
}

async function generateCodeChallenge(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function saveTokens(data) {
  localStorage.setItem(KEYS.accessToken, data.access_token);
  if (data.refresh_token)
    localStorage.setItem(KEYS.refreshToken, data.refresh_token);
  // 60s de margen para no usar un token a punto de caducar
  localStorage.setItem(
    KEYS.expiry,
    String(Date.now() + ((data.expires_in || 3600) - 60) * 1000)
  );
}

export function clearSpotifySession() {
  localStorage.removeItem(KEYS.accessToken);
  localStorage.removeItem(KEYS.refreshToken);
  localStorage.removeItem(KEYS.expiry);
  localStorage.removeItem(KEYS.verifier);
}

export function hasSpotifySession() {
  return Boolean(localStorage.getItem(KEYS.accessToken));
}

// Redirige a Spotify para autorizar (guarda el code_verifier para el retorno)
export async function redirectToSpotifyAuth() {
  const verifier = generateCodeVerifier();
  localStorage.setItem(KEYS.verifier, verifier);
  const challenge = await generateCodeChallenge(verifier);
  const params = new URLSearchParams({
    client_id: Globals.SPOTIFY_CLIENT_ID,
    response_type: "code",
    redirect_uri: Globals.REDIRECT_URI,
    scope: SCOPE,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  window.location.href = `${Globals.AUTH_ENDPOINT}?${params.toString()}`;
}

// Cambia el ?code= del retorno por access_token + refresh_token
export async function exchangeCodeForToken(code) {
  const verifier = localStorage.getItem(KEYS.verifier);
  if (!verifier) throw new Error("Sesión de Spotify inválida, vuelve a iniciar");
  const { data } = await axios.post(
    TOKEN_ENDPOINT,
    new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: Globals.REDIRECT_URI,
      client_id: Globals.SPOTIFY_CLIENT_ID,
      code_verifier: verifier,
    }),
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  );
  localStorage.removeItem(KEYS.verifier);
  saveTokens(data);
  return data.access_token;
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(KEYS.refreshToken);
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(
      TOKEN_ENDPOINT,
      new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: Globals.SPOTIFY_CLIENT_ID,
      }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );
    saveTokens(data);
    return data.access_token;
  } catch {
    clearSpotifySession();
    return null;
  }
}

// Devuelve un token vigente, renovándolo si caducó; null si hay que re-loguear
export async function getValidSpotifyToken() {
  const token = localStorage.getItem(KEYS.accessToken);
  const expiry = Number(localStorage.getItem(KEYS.expiry) || 0);
  if (token && Date.now() < expiry) return token;
  return refreshAccessToken();
}
